import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import uniqid from 'uniqid';

import {
    Autosuggest,
    Button,
    Checkbox,
    Container,
    DatePicker,
    Flashbar,
    FlashbarProps,
    FormField,
    Header,
    Input,
    KeyValuePairs,
    Select,
    SpaceBetween,
    Tabs
} from '@amzn/awsui-components-react';

import { useContinuousAuth } from '../../../pages/App';
import { useEmployeeData, UserRole } from '../../../common/EmployeeContext';
import { UserPermissions } from '../../../common/UserPermissions';
import {
    defaultEditorValue,
    getCsqaValidationRules,
} from '../../../common/ValidationUtils';
import settings from '../../../settings.json';
import HtmlRenderer from '../../../tiptap/HtmlRenderer';
import TextEditor from '../../../tiptap/TipTap';

import Styled from './styled';
import {
    BiQuestionTemplate,
    QualityQuestionTemplate,
    reviewTemplate,
} from '../reviewTemplate';
import { QualitySubAttribute } from '../types';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Variables
const { api_prefix: apiPrefix } = (settings as any)[window.location.host] || settings.default;

// Current Date
const currentDate = dayjs().utc().format('YYYY-MM-DD');

// Evaluation Types
const evaluationTypes = [
    { label: 'QA Audit', value: 'QA Audit' },
    { label: 'QA Coaching', value: 'QA Coaching' },
    { label: 'Calibration', value: 'Calibration' },
    { label: 'Supervisor Audit', value: 'Supervisor Audit' },
    { label: 'Training Audit', value: 'Training Audit' },
    { label: 'Bespoke Audit', value: 'Bespoke Audit' },
];

const monitoringMethods = [
    { label: 'Side-By-Side', value: 'Side-By-Side' },
    { label: 'Remote', value: 'Remote' },
    { label: 'Remote Live', value: 'Remote Live' },
];

const QUALITY_EXCEPTIONS = {
    questionIds: ['1.5', '1.9'],
    subAttributeIds: ['1.5.1', '1.9.1'],
};

type CsqaFormProps = {
    menuClick?: (title: string, path: string, param: string) => void;
    user: string;
    role?: UserRole;
    userPermissions: Set<string>;
    org: string;
    initialData?: any; // TODO: Update to form payload type
    onSubmit?: (reviewDetails: any) => void;
    isEditing?: boolean;
};

const CsqaForm: React.FC<CsqaFormProps> = (props) => {
    const {
        menuClick,
        userPermissions,
        org,
        initialData,
        onSubmit,
        isEditing = false,
    } = props;

    const [activeTab, setActiveTab] = useState('1.1');
    const [associateEmail, setAssociateEmail] = useState('');
    const [biAnswers, setBiAnswers] = useState<{
        [questionId: string]: { value: string; subvalue?: string }
    }>({});
    const [contactId, setContactId] = useState('');
    const [date, setDate] = useState(currentDate);
    const [evaluationType, setEvaluationType] = useState('');
    const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
    const [loading, setLoading] = useState(false);
    const [monitoringMethod, setMonitoringMethod] = useState('');
    const [notes, setNotes] = useState(defaultEditorValue);
    const [qualityAnswers, setQualityAnswers] = useState<{
        [questionId: string]: { value: 'Yes' | 'No' | 'N/A'; subAttributes: { [subAttrId: string]: boolean } };
    }>({});
    const [site, setSite] = useState('');
    const [supervisorEmail, setSupervisorEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const formId = useMemo(() => uniqid(), []);
    const { getHeaders } = useContinuousAuth();

    const {
        agents,
        leads,
        loading: employeesLoading,
        error: employeesError
    } = useEmployeeData();

    const associatesList = useMemo(() => {
        if (userPermissions.has(UserPermissions.CreateCsqa)) {
            const combined = [...agents, ...leads];
            return combined.sort((a, b) => a.Username.localeCompare(b.Username));
        }
        return agents;
    }, [agents, leads, userPermissions]);

    if (employeesError) {
        setFlashbarItems([{
            type: 'error',
            content: 'There was a problem fetching Agent Hierarchy',
            dismissible: true,
            onDismiss: () => setFlashbarItems([]),
            id: 'error-fetch-hierarchy'
        }]);
    }

    // Initialize form with initialData if available (Edit Support)
    useEffect(() => {
        if (initialData) {
            const { identifiers, qualityQuestions, biQuestions, notes } = initialData;

            // Update identifiers
            setAssociateEmail(identifiers.associateEmail || '');
            setEvaluationType(identifiers.evaluationType || '');
            setMonitoringMethod(identifiers.monitoringMethod || '');
            setContactId(identifiers.contactId || '');
            setSite(identifiers.associateSite || '');
            setSupervisorEmail(identifiers.supervisorEmail || '');

            // Transform qualityQuestions array into an object mapping questionId to its answer state.
            const qualityAnswers: {
                [questionId: string]: { value: 'Yes' | 'No' | 'N/A'; subAttributes: { [subAttrId: string]: boolean } }
            } = {};
            if (Array.isArray(qualityQuestions)) {
                qualityQuestions.forEach(question => {
                    qualityAnswers[question.questionId] = { value: question.value, subAttributes: {} };
                    if (question.subAttributes && Array.isArray(question.subAttributes)) {
                        question.subAttributes.forEach((sa: QualitySubAttribute) => {
                            qualityAnswers[question.questionId].subAttributes[sa.subAttributeId] = sa.value;
                        });
                    }
                });
            }
            setQualityAnswers(qualityAnswers);

            // Transform biQuestions array
            const biAnswers: {
                [questionId: string]: { value: string; subvalue?: string }
            } = {};
            if (Array.isArray(biQuestions)) {
                biQuestions.forEach(biQuestion => {
                    biAnswers[biQuestion.questionId] = { value: biQuestion.value, subvalue: biQuestion.subvalue || '' };
                });
            }
            setBiAnswers(biAnswers);
            setNotes(notes);
        }
    }, [initialData]);

    const associatesOptionsList = useMemo(() => {
        return associatesList?.map(agent => ({ value: agent.Username, label: agent.Username }));
    }, [associatesList]);

    const setTmDetails = useCallback((selectedOption: { label: string; value: string }) => {
        const value = selectedOption.value;
        const found = associatesList.find(agent => agent.Username === value);
        if (found) {
            setAssociateEmail(value);
            setSite(found.Site || '');
            setSupervisorEmail(found.Supervisor || '');
        }
    }, [associatesList]);

    // QUALITY
    const qualityComplete = useMemo(() => {
        return Object.keys(reviewTemplate.qualityQuestions).every(qualityQuestionId => {
            const answer = qualityAnswers[qualityQuestionId]?.value;
            // no answer provided
            if (!answer) return false;
            if (answer === 'No') {
                // Check that at least one sub-attribute is true
                const subAttrs = qualityAnswers[qualityQuestionId]?.subAttributes;
                return subAttrs && Object.values(subAttrs).some(val => val === true);
            }
            // For 'Yes' or 'N/A', we consider it complete.
            return answer === 'Yes' || answer === 'N/A';
        });
    }, [qualityAnswers]);

    const qualityQuestionTabs = useMemo(() => {
        const qualityQuestionIds = Object.keys(reviewTemplate.qualityQuestions);
        return qualityQuestionIds.map((qualityQuestionId, index) => {
            const questionTemplate = reviewTemplate.qualityQuestions[qualityQuestionId] as QualityQuestionTemplate;
            const answer = qualityAnswers[qualityQuestionId]?.value;
            const hasSelectedSub = qualityAnswers[qualityQuestionId]?.subAttributes
                ? Object.values(qualityAnswers[qualityQuestionId].subAttributes).some(val => val)
                : false;
            const label = (
                <>
                    {`Question ${index + 1} `}
                    {answer === 'Yes' && <span style={{ color: 'green' }}>✓</span>}
                    {answer === 'No' && (
                        !hasSelectedSub ?
                            <span style={{ color: 'yellow' }}>⚠</span> :
                            <span style={{ color: 'red' }}>✗</span>
                    )}
                    {answer === 'N/A' && <span style={{ color: '#999' }}>N/A</span>}
                </>
            );
            let guidelinesLabel = `Question ${index + 1} `;
            let guidelinesIntent = questionTemplate.intent;
            let guidelinesPass = questionTemplate.passCriteria;
            let guidelinesFail = questionTemplate.failCriteria;

            if (answer === 'No' && qualityAnswers[qualityQuestionId]?.subAttributes) {
                Object.entries(questionTemplate.subAttributes).forEach(([subId, subTemplate]) => {
                    if (qualityAnswers[qualityQuestionId].subAttributes[subId]) {
                        guidelinesLabel = subTemplate.label;
                        guidelinesIntent = subTemplate.intent;
                        guidelinesPass = subTemplate.passCriteria;
                        guidelinesFail = subTemplate.failCriteria;
                    }
                });
            }
            return {
                id: qualityQuestionId,
                label,
                content: (
                    <SpaceBetween size='m' direction='vertical'>
                        <Styled.QualityQuestionLabel>{questionTemplate.question}</Styled.QualityQuestionLabel>
                        <Styled.QualityQuestionAnswer>
                            <FormField key={qualityQuestionId}>
                                <Select
                                    selectedOption={qualityAnswers[qualityQuestionId]?.value ? { value: qualityAnswers[qualityQuestionId].value, label: qualityAnswers[qualityQuestionId].value } : null}
                                    onChange={(e) => {
                                        const newValue = e.detail.selectedOption.value as 'Yes' | 'No' | 'N/A';
                                        setQualityAnswers(prev => ({
                                            ...prev,
                                            [qualityQuestionId]: {
                                                value: newValue,
                                                subAttributes: newValue === 'No' ? (prev[qualityQuestionId]?.subAttributes || {}) : {},
                                            }
                                        }));
                                    }}
                                    options={[
                                        { label: 'Yes', value: 'Yes' },
                                        { label: 'No', value: 'No' },
                                        { label: 'N/A', value: 'N/A' },
                                    ]}
                                    placeholder='Select Answer'
                                />
                            </FormField>
                        </Styled.QualityQuestionAnswer>
                        {qualityAnswers[qualityQuestionId]?.value === 'No' && (
                            <div style={{ marginLeft: '1rem' }}>
                                {Object.entries(questionTemplate.subAttributes).map(([subId, subTemplate]) => {
                                    const checked = qualityAnswers[qualityQuestionId]?.subAttributes[subId] || false;
                                    return (
                                        <FormField key={subId}>
                                            <Checkbox
                                                checked={checked}
                                                onChange={(e) => {
                                                    const newChecked = e.detail.checked;
                                                    setQualityAnswers(prev => ({
                                                        ...prev,
                                                        [qualityQuestionId]: {
                                                            ...prev[qualityQuestionId],
                                                            subAttributes: {
                                                                ...prev[qualityQuestionId]?.subAttributes,
                                                                [subId]: newChecked,
                                                            }
                                                        }
                                                    }));
                                                }}
                                            >
                                                {subTemplate.label}
                                            </Checkbox>
                                        </FormField>
                                    );
                                })}
                            </div>
                        )}
                        <Styled.EvaluationGuidelines>
                            <Styled.EvaluationGuidelinesHeader>
                                Delivering IMPACT Guidelines - {guidelinesLabel}
                            </Styled.EvaluationGuidelinesHeader>
                            <KeyValuePairs
                                columns={3}
                                minColumnWidth={300}
                                items={[
                                    { label: 'Intent', value: <HtmlRenderer html={guidelinesIntent} /> },
                                    { label: 'Pass Criteria', value: <HtmlRenderer html={guidelinesPass} /> },
                                    { label: 'Fail Criteria', value: <HtmlRenderer html={guidelinesFail} /> },
                                ]}
                            />
                        </Styled.EvaluationGuidelines>
                    </SpaceBetween>
                )
            };
        });
    }, [qualityAnswers]);

    // Check for Quality miss, if true we create an associated Coaching record
    const qualityQuestionMiss = useMemo(() =>
        Object.keys(reviewTemplate.qualityQuestions).some(qualityQuestionId => {
            const answer = qualityAnswers[qualityQuestionId];
            const answerIsNo = answer?.value === 'No';
            const selectedSubIds = answer?.subAttributes
                ? Object.keys(answer.subAttributes).filter(subId => answer.subAttributes[subId])
                : [];

            // Check for the exception specific to questions '1.5' and '1.9'
            // If only one sub-attribute is selected and its label is 'Not Associate Related',
            // then the answer should not count as a quality question miss.
            const exception = QUALITY_EXCEPTIONS.questionIds.includes(qualityQuestionId) &&
                selectedSubIds.length === 1 &&
                QUALITY_EXCEPTIONS.subAttributeIds.includes(selectedSubIds[0]);

            // A question counts as a miss if it is answered 'No' and does not meet the exception criteria.
            return answerIsNo && !exception;
        }), [qualityAnswers]);

    // BUSINESS IMPACT
    const filteredBiQuestions = useMemo(() => {
        return Object.entries(reviewTemplate.biQuestions)
            .filter(([questionId, questionTemplate]) => {
                if (questionTemplate.triggerConditions && questionTemplate.triggerConditions.length > 0) {
                    // Only include if every trigger condition is met
                    return questionTemplate.triggerConditions.every(condition => {
                        const qualityAnswer = qualityAnswers[condition.qualityQuestionId];
                        return qualityAnswer && qualityAnswer.value === 'No' && qualityAnswer.subAttributes[condition.subAttributeId];
                    });
                }
                // No trigger conditions? Always include.
                return true;
            })
            .map(([questionId, questionTemplate]) => ({ questionId, questionTemplate }));
    }, [qualityAnswers]);

    const businessImpactComplete = useMemo(() =>
        filteredBiQuestions.every(({ questionId, questionTemplate }) => {
            const answer = biAnswers[questionId];
            const hasValidAnswer = Boolean(answer?.value);
            const primaryOptions = Object.entries(questionTemplate.options).map(
                ([, optionData]) => ({
                    label: optionData.label,
                    value: optionData.label,
                    subValues: optionData.subValues || []
                })
            );
            const selectedPrimary = primaryOptions.find(option => option.value === answer?.value);
            const requiresSubValue = ((selectedPrimary?.subValues) || []).length > 0;
            // If a subvalue is required, validate that it exists and is not just whitespace.
            const isSubValueValid = requiresSubValue ? Boolean(answer?.subvalue?.trim()) : true;

            // The question is complete if it has a valid answer and, if needed, a valid subvalue.
            return hasValidAnswer && isSubValueValid as boolean;
        }), [filteredBiQuestions, biAnswers]);

    const biQuestionsTab = useMemo(() => {
        if (filteredBiQuestions.length === 0) {
            return <div>No BI questions applicable.</div>;
        }

        return filteredBiQuestions.map(({ questionId, questionTemplate }) => {
            const currentAnswer = biAnswers[questionId] || { value: '', subvalue: '' };

            // Build the primary options from the BI question's options.
            const primaryOptions = Object.entries(questionTemplate.options).map(
                ([, optionData]) => ({
                    label: optionData.label,
                    value: optionData.label,
                    subValues: optionData.subValues || []
                })
            );

            const selectedPrimary = primaryOptions.find(
                option => option.value === currentAnswer.value
            ) || null;

            // If sub-values exist, build the sub-options.
            const subOptions = selectedPrimary && selectedPrimary.subValues.length > 0
                ? selectedPrimary.subValues.map(sub => ({ label: sub, value: sub }))
                : [];

            const selectedSubOption = subOptions.find(
                option => option.value === currentAnswer.subvalue
            ) || null;

            return (
                <Styled.BiQuestionWrapper key={questionId}>
                    <Styled.BiQuestionLabel>{questionTemplate.question}</Styled.BiQuestionLabel>
                    <Styled.BiQuestionAnswer>
                        <FormField>
                            <Select
                                selectedOption={selectedPrimary}
                                onChange={(e) => {
                                    const newValue = e.detail.selectedOption.value || '';
                                    setBiAnswers(prev => ({
                                        ...prev,
                                        [questionId]: { value: newValue, subvalue: '' }
                                    }));
                                }}
                                placeholder='Select Answer'
                                options={primaryOptions}
                            />
                        </FormField>
                        {selectedPrimary && selectedPrimary.subValues.length > 0 && (
                            <FormField>
                                <Select
                                    selectedOption={selectedSubOption}
                                    onChange={(e) => {
                                        const newSub = e.detail.selectedOption.value;
                                        setBiAnswers(prev => ({
                                            ...prev,
                                            [questionId]: { ...prev[questionId], subvalue: newSub }
                                        }));
                                    }}
                                    placeholder='Select Supporting Detail'
                                    options={subOptions}
                                />
                            </FormField>
                        )}
                    </Styled.BiQuestionAnswer>
                </Styled.BiQuestionWrapper>
            );
        });
    }, [filteredBiQuestions, biAnswers]);

    const businessImpactTabLabel = (
        <>
            Business Impact {businessImpactComplete ?
                <span style={{ color: 'green' }}>✓</span> :
                <span style={{ color: 'yellow' }}>⚠</span>
            }
        </>
    );

    const allTabs = useMemo(() => {
        const tabs = [...qualityQuestionTabs];
        if (qualityComplete) {
            tabs.push({
                id: 'businessImpact',
                label: businessImpactTabLabel,
                content: (
                    <SpaceBetween size='m' direction='vertical'>
                        {biQuestionsTab}
                    </SpaceBetween>
                )
            });
        }
        return tabs;
    }, [qualityQuestionTabs, qualityComplete, biQuestionsTab, businessImpactTabLabel]);


    const inputValidationCheck = useCallback(() => {
        const errors: string[] = [];

        const validationRules = getCsqaValidationRules(
            {
                associateEmail,
                contactId,
                date,
                evaluationType,
                monitoringMethod,
                notes,
                supervisorEmail,
            },
            qualityAnswers,
            biAnswers,
            filteredBiQuestions,
        );

        // Iterate over validation rules and collect errors
        validationRules.forEach(rule => {
            if (!rule.test()) {
                errors.push(rule.message);
            }
        });

        return errors;
    }, [
        associateEmail,
        contactId,
        date,
        defaultEditorValue,
        evaluationType,
        monitoringMethod,
        notes,
        supervisorEmail,
        qualityAnswers,
        biAnswers,
        filteredBiQuestions,
    ]);

    const createAssociatedCoachingRecord = useCallback(async (csqaReviewId: string) => {
        const qualityMisses = Object.keys(qualityAnswers).reduce((acc: string[], qualityQuestionId) => {
            if (qualityAnswers[qualityQuestionId]?.value === 'No') {
                // Get the main question text from the template
                const questionLabel = reviewTemplate.qualityQuestions[qualityQuestionId]?.question;
                // Get the sub-attributes template for this question
                const subAttributes = reviewTemplate.qualityQuestions[qualityQuestionId]?.subAttributes;
                // From the current answer, collect the labels of any sub-attributes that are checked
                const selectedSubLabels = Object.keys(qualityAnswers[qualityQuestionId].subAttributes || {})
                    .filter(subId => qualityAnswers[qualityQuestionId].subAttributes[subId])
                    .map(subId => subAttributes[subId]?.label);
                // If any sub-attributes are selected, include them; otherwise, include the question label
                if (selectedSubLabels.length > 0) {
                    acc.push(`${questionLabel}: ${selectedSubLabels.join(', ')}`);
                } else {
                    acc.push(questionLabel);
                }
            }
            return acc;
        }, []);

        // Create the additionalNotes string. If no quality misses, note 'None'
        let additionalNotes = '<strong>Quality Miss:</strong><br/>';
        if (qualityMisses.length > 0) {
            additionalNotes += qualityMisses.map(item => ` • ${item}<br/>`).join('');
        } else {
            additionalNotes += 'None';
        }

        const requestPayload = {
            action: 'No action required',
            agent: associateEmail,
            additionalNotes: additionalNotes,
            category: 'Performance',
            createdDate: currentDate,
            date,
            deleted: false,
            dueDate: dayjs().add(5, 'days').format('YYYY-MM-DD'),
            file: {},
            formId,
            goodStanding: true,
            hrReview: false,
            location: site,
            notes: notes.toString(),
            reason: 'QA Review',
            status: 'Ready for Delivery',
            supervisor: supervisorEmail,
            csqaReviewId,
        };
        const headers = await getHeaders();
        await axios.put(apiPrefix + '/api/coaching-tool', requestPayload, { headers });
    }, [
        associateEmail,
        currentDate,
        date,
        formId,
        notes,
        qualityAnswers,
        site,
        supervisorEmail,
        getHeaders,
    ]);

    // Helper functions to transform quality and BI answers
    const transformQualityQuestions = (): any[] => {
        return Object.keys(reviewTemplate.qualityQuestions).map((qualityQuestionId) => {
            const template = reviewTemplate.qualityQuestions[qualityQuestionId] as QualityQuestionTemplate;
            const answer = qualityAnswers[qualityQuestionId] || { value: '', subAttributes: {} };
            // Only include sub-attributes if answer is 'No'
            const subAttributesArray =
                answer.value === 'No'
                    ? Object.keys(template.subAttributes).map((subId) => ({
                        subAttributeId: subId,
                        label: template.subAttributes[subId].label,
                        value: answer.subAttributes[subId] || false,
                    }))
                    : [];
            return {
                questionId: qualityQuestionId,
                label: template.question,
                value: answer.value,
                // Include subAttributes only if applicable
                ...(answer.value === 'No' ? { subAttributes: subAttributesArray } : {}),
            };
        });
    };

    const transformBiQuestions = (): any[] => {
        // We only want to include BI questions that are applicable.
        // For each BI question, if triggerConditions exist, we check them.
        return Object.entries(reviewTemplate.biQuestions)
            .filter(([qualityQuestionId, template]) => {
                const biTemplate = template as any;
                if (biTemplate.triggerConditions && biTemplate.triggerConditions.length > 0) {
                    return biTemplate.triggerConditions.every((condition: any) => {
                        const qualityAnswer = qualityAnswers[condition.qualityQuestionId];
                        return qualityAnswer && qualityAnswer.value === 'No' && qualityAnswer.subAttributes[condition.subAttributeId];
                    });
                }
                return true;
            })
            .map(([qualityQuestionId, template]) => {
                const biTemplate = template as any;
                const answer = biAnswers[qualityQuestionId] || { value: '', subvalue: '' };
                return {
                    questionId: qualityQuestionId,
                    label: biTemplate.question,
                    value: answer.value,
                    ...(answer.subvalue ? { subvalue: answer.subvalue } : {}),
                };
            });
    };

    const handleSubmit = useCallback(async () => {
        setSubmitted(false);
        const formErrors = inputValidationCheck();
        if (formErrors.length > 0) {
            setFlashbarItems([{
                type: 'error',
                content: (
                    <>
                        Please resolve the fields listed below:
                        {formErrors.map((error, index) => (
                            <Styled.InputValidationErrors key={index}>
                                - {error}
                            </Styled.InputValidationErrors>
                        ))}
                    </>
                ),
                dismissible: true,
                onDismiss: () => setFlashbarItems([]),
                id: 'error-1'
            }]);
            return;
        }

        setLoading(true);
        const headers = await getHeaders();
        // Transform Quality and BI Answers to match expected schema
        const qualityQuestions = transformQualityQuestions();
        const biQuestions = transformBiQuestions();

        // Construct the payload
        const requestPayload = {
            versionId: reviewTemplate.version,
            identifiers: {
                associateEmail,
                associateSite: site,
                contactId,
                coachingStatus: qualityQuestionMiss ? 'required' : 'n/a',
                deleted: initialData?.deleted || false,
                evaluationType,
                formId,
                monitoringMethod,
                supervisorEmail,
            },
            qualityQuestions,
            biQuestions,
            notes: notes.toString(),
        };

        try {
            if (isEditing) {
                // **Edit Mode**: Delegate the update to the parent component
                onSubmit && onSubmit(requestPayload);
                setSubmitted(true);
                setFlashbarItems([{
                    type: 'success',
                    content: `Success! Your changes are now showing in the Dashboard.`,
                    dismissible: false,
                    onDismiss: () => setFlashbarItems([]),
                    id: 'success-1',
                    action: (
                        <Button
                            variant='normal'
                            onClick={() => menuClick?.('CSQA Dashboard', 'csqa', '')}
                        >
                            Go to Dashboard
                        </Button>
                    )
                }]);
            } else {
                // Create csqa review
                const response = await axios.put(apiPrefix + '/api/csqa', requestPayload, { headers });
                const csqaReviewId = response.data.id;
                if (!csqaReviewId) {
                    throw new Error('CSQA Document ID not returned');
                }

                // If there was a qualityQuesitonMiss and org = Ring, create associated coaching record
                if (org === 'Ring' && qualityQuestionMiss) {
                    await createAssociatedCoachingRecord(csqaReviewId);
                }
                setSubmitted(true);
                setFlashbarItems([{
                    type: 'success',
                    content: `Success! Form ID: ${requestPayload.identifiers.formId}. A copy of your completed coaching has been delivered via email.`,
                    dismissible: true,
                    onDismiss: () => setFlashbarItems([]),
                    id: 'success-1',
                    action: (
                        <Button
                            variant='normal'
                            onClick={() => menuClick?.('CSQA Dashboard', 'csqa', '')}
                        >
                            Go to Dashboard
                        </Button>
                    )
                }]);

                // Call onSubmit prop with updated data
                if (onSubmit) {
                    onSubmit(requestPayload);
                }
            }
        } catch (e: any) {
            const errorMessage = isEditing
                ? (e?.response?.data?.message || 'There was a problem updating the review.')
                : (e?.response?.data?.message || 'There was a problem creating the associated Coaching Record');
            console.error(`${isEditing ? 'update' : 'creation'} error`, errorMessage);
            setFlashbarItems([{
                type: 'error',
                content: errorMessage,
                dismissible: true,
                onDismiss: () => setFlashbarItems([]),
                id: `error-${isEditing ? 'update-review' : 'create-coaching-record'}`
            }]);
        } finally {
            setLoading(false);
        }
    }, [
        associateEmail,
        evaluationType,
        currentDate,
        createAssociatedCoachingRecord,
        date,
        formId,
        inputValidationCheck,
        getHeaders,
        isEditing,
        contactId,
        menuClick,
        notes,
        org,
        qualityAnswers,
        biAnswers,
        site,
        supervisorEmail,
        initialData,
        onSubmit
    ]);

    // Convert DatePicker's output to YYYY-MM-DD
    const handleDateChange = useCallback((e: any) => {
        const value = e.detail.value; // YYYY/MM/DD
        if (value) {
            const formatted = dayjs(value, 'YYYY/MM/DD').format('YYYY-MM-DD');
            setDate(formatted);
        } else {
            setDate('');
        }
    }, []);

    return (
        <Styled.FormBody>
            {flashbarItems.length > 0 && <Styled.Banner><Flashbar items={flashbarItems} /></Styled.Banner>}
            <Container
                header={<Header variant='h2'>Contact Attributes</Header>}
            >
                <SpaceBetween direction='horizontal' size='m' alignItems='start'>
                    <FormField label='Associate Email'>
                        <Autosuggest
                            value={associateEmail}
                            onChange={e => setAssociateEmail(e.detail.value)}
                            onSelect={e => {
                                const selectedValue = e.detail.value;
                                const found = associatesList.find(a => a.Username === selectedValue);
                                if (found) {
                                    setTmDetails({
                                        label: found.Username,
                                        value: found.Username
                                    });
                                }
                            }}
                            options={associatesOptionsList}
                            placeholder='Associate Email'
                        />
                    </FormField>

                    <FormField label='Supervisor Email'>
                        <Select
                            selectedOption={supervisorEmail ? { value: supervisorEmail, label: supervisorEmail } : null}
                            disabled
                            options={[]}
                            placeholder='Supervisor Associated'
                        />
                    </FormField>

                    <FormField label='Evaluation Type'>
                        <Select
                            selectedOption={evaluationType ? { value: evaluationType, label: evaluationType } : null}
                            onChange={e => {
                                const selected = e.detail.selectedOption;
                                setEvaluationType(selected?.value || '');
                            }}
                            options={evaluationTypes}
                            placeholder='Evaluation Type'
                        />
                    </FormField>

                    <FormField label='Monitoring Method'>
                        <Select
                            selectedOption={monitoringMethod ? { value: monitoringMethod, label: monitoringMethod } : null}
                            onChange={e => {
                                const selected = e.detail.selectedOption;
                                setMonitoringMethod(selected?.value || '');
                            }}
                            options={monitoringMethods}
                            placeholder='Monitoring Method'
                        />
                    </FormField>

                    <FormField label='Contact ID'>
                        <Input
                            value={contactId}
                            onChange={e => setContactId(e.detail.value)}
                            placeholder='Contact ID'
                        />
                    </FormField>

                    <FormField label='Date'>
                        <DatePicker
                            onChange={handleDateChange}
                            value={currentDate}
                            disabled
                            placeholder='Select Date'
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
            <Styled.FormDetails>

                <Container header={<Header variant='h2'>Evaluation Details</Header>} className='review-details'>
                    <SpaceBetween size='m' direction='vertical'>
                        <Tabs
                            tabs={allTabs}
                            activeTabId={activeTab}
                            onChange={(e) => {
                                setActiveTab(e.detail.activeTabId);
                            }}
                        />
                        {!isEditing && (
                            <>
                                <Header variant='h3'> Notes / Comments </Header>
                                <FormField stretch>
                                    <TextEditor
                                        value={notes}
                                        onChange={(value: string) => setNotes(value)}
                                    />
                                </FormField>
                            </>
                        )}
                    </SpaceBetween>
                    <Styled.Submit>
                        <Button
                            variant='primary'
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={loading || submitted}
                        >
                            {loading ? 'Loading...' : submitted ? 'Success!' : 'Submit'}
                        </Button>
                    </Styled.Submit>
                </Container>
            </Styled.FormDetails>
        </Styled.FormBody>
    );
};

export default CsqaForm;
